import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, VStack, Text, Box, HStack, Icon, Input, SimpleGrid, useToast, Badge,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useDisclosure,
} from '@chakra-ui/react';
import { FiUpload, FiTrash2 } from 'react-icons/fi';
import { limpiezaCamionesApi, UPLOADS_BASE, SmArchivo } from '../../../api/serviciosApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  registro: any;
  onGuardado: () => void;
}

const MOMENTOS: ('ANTES' | 'DESPUES')[] = ['ANTES', 'DESPUES'];

const ModalLimpiezaCamionEvidencias: React.FC<Props> = ({ isOpen, onClose, registro, onGuardado }) => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);
  const antesRef = useRef<HTMLInputElement>(null);
  const despuesRef = useRef<HTMLInputElement>(null);
  const { isOpen: isOpenDel, onOpen: onOpenDel, onClose: onCloseDel } = useDisclosure();

  const [detalle, setDetalle] = useState<any>(registro);
  const [comentario, setComentario] = useState('');
  const [subiendo, setSubiendo] = useState<string | null>(null);
  const [archivoDel, setArchivoDel] = useState<SmArchivo | null>(null);
  const [eliminando, setEliminando] = useState(false);

  const recargar = useCallback(async () => {
    if (!registro?.id) return;
    try {
      const res = await limpiezaCamionesApi.get(registro.id);
      setDetalle(res);
    } catch {
      setDetalle(registro);
    }
  }, [registro]);

  useEffect(() => { if (isOpen) recargar(); }, [isOpen, recargar]);

  const archivos: SmArchivo[] = detalle?.archivos || [];
  const porMomento = (m: string) => archivos.filter((a) => a.momento === m);

  const handleSubir = async (momento: string, file?: File) => {
    if (!file) return;
    setSubiendo(momento);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      fd.append('momento', momento);
      fd.append('tipoArchivo', 'foto');
      if (comentario) fd.append('comentario', comentario);
      await limpiezaCamionesApi.addArchivo(detalle.id, fd);
      toast({ title: 'Evidencia subida', status: 'success', duration: 2000 });
      setComentario('');
      await recargar();
      onGuardado();
    } catch (error: any) {
      toast({ title: 'Error al subir', description: error?.response?.data?.message, status: 'error', duration: 3000 });
    } finally {
      setSubiendo(null);
    }
  };

  const handleEliminar = (a: SmArchivo) => { setArchivoDel(a); onOpenDel(); };

  const confirmarEliminar = async () => {
    if (!archivoDel) return;
    setEliminando(true);
    try {
      await limpiezaCamionesApi.removeArchivo(detalle.id, archivoDel.id);
      toast({ title: 'Evidencia eliminada', status: 'success', duration: 2000 });
      onCloseDel();
      await recargar();
      onGuardado();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message, status: 'error', duration: 3000 });
    } finally {
      setEliminando(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
            Evidencias — {detalle?.folio || detalle?.camion?.numeroEconomico || `Registro #${detalle?.id}`}
          </ModalHeader>
          <ModalBody py={5}>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontSize="xs" color="gray.500" mb={1}>Comentario (opcional, se aplica a la siguiente carga)</Text>
                <Input size="sm" placeholder="Comentario para la evidencia..." value={comentario} onChange={(e) => setComentario(e.target.value)} />
              </Box>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {MOMENTOS.map((m) => (
                  <Box key={m} border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
                    <HStack justify="space-between" mb={2}>
                      <Badge colorScheme={m === 'ANTES' ? 'orange' : 'green'} px={2} py={0.5} borderRadius="full">{m}</Badge>
                      <Text fontSize="xs" color="gray.400">{porMomento(m).length} foto(s)</Text>
                    </HStack>
                    <input
                      ref={m === 'ANTES' ? antesRef : despuesRef}
                      type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={(e) => { handleSubir(m, e.target.files?.[0]); e.target.value = ''; }}
                    />
                    <Button
                      size="sm" w="full" mb={3} leftIcon={<Icon as={FiUpload} />} variant="outline" colorScheme="teal"
                      isLoading={subiendo === m} loadingText="Subiendo..."
                      onClick={() => (m === 'ANTES' ? antesRef : despuesRef).current?.click()}
                    >
                      Agregar foto {m}
                    </Button>
                    <HStack spacing={2} flexWrap="wrap">
                      {porMomento(m).length === 0 ? (
                        <Text fontSize="xs" color="gray.400">Sin evidencias</Text>
                      ) : porMomento(m).map((a) => (
                        <Box key={a.id} position="relative" w="80px" h="80px">
                          <Box as="a" href={`${UPLOADS_BASE}${a.rutaArchivo}`} target="_blank" display="block" w="80px" h="80px"
                            borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.200">
                            <img src={`${UPLOADS_BASE}${a.rutaArchivo}`} alt={a.nombreArchivo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </Box>
                          <Button size="xs" position="absolute" top="2px" right="2px" colorScheme="red"
                            borderRadius="full" p={0} minW="18px" h="18px" onClick={() => handleEliminar(a)}>
                            <Icon as={FiTrash2} boxSize={2.5} />
                          </Button>
                        </Box>
                      ))}
                    </HStack>
                  </Box>
                ))}
              </SimpleGrid>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isOpenDel} leastDestructiveRef={cancelRef} onClose={onCloseDel}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader color="#5b182b">Eliminar evidencia</AlertDialogHeader>
            <AlertDialogBody>¿Eliminar <strong>{archivoDel?.nombreArchivo}</strong>? Esta acción no se puede deshacer.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseDel} isDisabled={eliminando}>Cancelar</Button>
              <Button colorScheme="red" ml={3} onClick={confirmarEliminar} isLoading={eliminando} loadingText="Eliminando...">Eliminar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default ModalLimpiezaCamionEvidencias;
